import {
    Component,
    createSignal,
    For,
    onCleanup,
    onMount,
    Show,
} from 'solid-js'

import {
    api_admin_helmet_extensions_list,
    api_companies_helmets_extensions_list,
    Helmet,
    HelmetExtension,
} from 'abi'
import { addAlert, LoadingElem } from 'components'
import { helmet_ext_to_image, helmet_to_image } from 'helmet-ig'
import { CloseIcon, EditIcon, PrintIcon, WarningIcon } from 'icons'
import { createStore } from 'solid-js/store'
import { isAdmin } from '../users/corp/shared'
import './style/print.scss'

interface PrintHelmetProps {
    helmet: Helmet

    onClose(): void
    onPrint(): void
    onExtention(): void
    onExtentionPrint(c: HelmetExtension): void
}
const PrintHelmet: Component<PrintHelmetProps> = P => {
    let imgRef: HTMLImageElement

    type T = {
        extentions: HelmetExtension[]
        loading: boolean
    }
    const [state, setState] = createStore<T>({
        loading: true,
        extentions: [],
    })

    onMount(async () => {
        let h = P.helmet

        let objectUrl

        setState('loading', true)

        getExtentions(h)

        try {
            const res = await helmet_to_image(h)

            objectUrl = URL.createObjectURL(res)

            imgRef.src = objectUrl
        } catch (err) {
            addAlert({
                type: 'error',
                subject: 'خطا!',
                content: err instanceof Error ? err.message : String(err),
                timeout: 3,
            })
            return
        } finally {
        }

        onCleanup(() => {
            objectUrl && URL.revokeObjectURL(objectUrl)
        })
    })

    onCleanup(() => {
        closed()
    })

    const getExtentions = async (h: Helmet) => {
        let res

        if (isAdmin()) {
            res = await api_admin_helmet_extensions_list({
                helmet: h.id,
                page: 0,
            })
        } else {
            if (!h.company) return

            res = await api_companies_helmets_extensions_list({
                hid: h.id,
                cid: h.company,
            })
        }

        if (!res.ok()) return

        setState('extentions', res.body)
    }

    const closed = () => {}

    const submitForm = () => {
        if (state.loading) {
            addAlert({
                type: 'error',
                subject: 'تصویر هنوز آماده نیست',
                content: 'لطفا کمی صبر کنید تا تصویر بارگذاری شود.',
                timeout: 3,
            })
            return
        }

        if (!imgRef?.src) {
            addAlert({
                type: 'error',
                subject: 'آدرس تصویر موجود نیست',
                content: 'تصویر بارگذاری نشده یا آدرسی موجود نیست.',
                timeout: 3,
            })
            return
        }

        const src = imgRef.src
        const w = window.open('', '_blank')
        if (!w) {
            addAlert({
                type: 'error',
                subject: 'پنجره جدید باز نشد',
                content:
                    'مرورگر اجازه باز کردن پنجره جدید را نداد (popup blocker). لطفا اجازه دهید یا از دکمه راست استفاده کنید.',
                timeout: 4,
            })
            return
        }

        const cleanup = () => {
            try {
                w.removeEventListener &&
                    w.removeEventListener('afterprint', afterPrintHandler)
            } catch {}
        }

        const afterPrintHandler = () => {
            try {
                P.onPrint() // best-effort callback
            } catch (e) {
                /* ignore errors from user callback */
            } finally {
                cleanup()
            }
        }

        try {
            const doc = w.document
            doc.title = 'Print'

            const style = doc.createElement('style')
            style.textContent = `
                @page {
                    size: auto;
                    margin: 0;
                }

                html,
                body {
                    margin: 0;
                    padding: 0;
                    width: 100%;
                    height: 100%;
                }

                body {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    overflow: hidden;
                }

                img {
                    display: block;
                    width: auto;
                    height: auto;
                    max-width: 100%;
                    max-height: 100%;
                    object-fit: contain;
                    page-break-inside: avoid;
                    break-inside: avoid;
                }

                @media print {
                    html,
                    body {
                        width: 100%;
                        height: 100%;
                        margin: 0;
                        padding: 0;
                    }

                    img {
                        max-width: 100%;
                        max-height: 100%;
                        width: auto;
                        height: auto;
                    }
                }
            `
            doc.head.appendChild(style)

            const outImg = doc.createElement('img')
            outImg.src = src
            outImg.alt = 'Helmet Image'

            // attach afterprint listener on the popup window
            // note: some browsers fire afterprint on the popup window; keep a fallback below
            try {
                w.addEventListener('afterprint', afterPrintHandler)
            } catch (e) {
                // ignore if not supported
            }

            const doPrintAndClose = () => {
                try {
                    w.focus()
                    w.print()
                } catch (e) {
                    addAlert({
                        type: 'error',
                        subject: 'خطا در چاپ',
                        content: String(e),
                        timeout: 4,
                    })
                } finally {
                    // close the popup shortly after print dialog closes
                    setTimeout(() => {
                        try {
                            w.close()
                        } catch {}
                    }, 200)
                }
            }

            const onLoad = () => {
                // update @page orientation based on image natural size
                // const isLandscape = outImg.naturalWidth > outImg.naturalHeight
                // style.textContent = style.textContent!.replace(
                //     /@page\s*\{[^}]*\}/,
                //     `@page { size: A4 ${isLandscape ? 'landscape' : 'portrait'}; margin: 0; }`
                // )

                doPrintAndClose()
            }

            const onError = () => {
                addAlert({
                    type: 'error',
                    subject: 'خطا در بارگذاری تصویر',
                    content: 'تصویر در صفحه چاپ بارگذاری نشد.',
                    timeout: 4,
                })
                setTimeout(() => {
                    try {
                        w.close()
                    } catch {}
                }, 300)
            }

            outImg.addEventListener('load', onLoad, { once: true })
            outImg.addEventListener('error', onError, { once: true })
            doc.body.appendChild(outImg)
        } catch (err) {
            addAlert({
                type: 'error',
                subject: 'خطا!',
                content: err instanceof Error ? err.message : String(err),
                timeout: 4,
            })
            try {
                w.close()
            } catch {}
        }
    }

    interface ExtentionRowProps {
        onClick(): void
        index: number
    }
    const ExtentionRow: Component<ExtentionRowProps> = E => {
        return (
            <div class='extention-row' role='button' onclick={E.onClick}>
                <div class='icon'>
                    <WarningIcon />
                </div>
                <span class='title_smaller'>الحاقیه شماره {E.index}</span>
            </div>
        )
    }

    return (
        <div class='print-cmp-container' onclick={P.onClose}>
            <form
                onsubmit={e => {
                    e.preventDefault()
                    submitForm()
                }}
                class='print-cmp-wrapper'
                onclick={e => e.stopPropagation()}
            >
                <div class='scaler'></div>

                <Show when={state.extentions}>
                    <div class='extentions'>
                        <For each={state.extentions}>
                            {(ext, i) => (
                                <ExtentionRow
                                    onClick={() => P.onExtentionPrint(ext)}
                                    index={i() + 1}
                                />
                            )}
                        </For>
                    </div>
                </Show>

                <div class='card'>
                    <button
                        class='close-print'
                        type='button'
                        onclick={P.onClose}
                    >
                        <CloseIcon />
                    </button>

                    <div
                        class='img-container'
                        classList={{ loaded: !state.loading }}
                    >
                        <LoadingElem class='img-loading' />
                        <img
                            decoding='async'
                            loading='lazy'
                            src=''
                            alt=''
                            onload={() => setState('loading', false)}
                            ref={e => (imgRef = e)}
                        />
                    </div>
                    <div class='cta-wrapper'>
                        <button
                            class='title_small main'
                            disabled={state.loading}
                        >
                            <PrintIcon />
                            پرینت
                        </button>
                        <Show when={P.helmet.company}>
                            <button
                                type='button'
                                class='elsagh title_small'
                                onclick={P.onExtention}
                            >
                                <EditIcon />
                                الحاقیه
                            </button>
                        </Show>
                    </div>
                </div>
            </form>
        </div>
    )
}

interface PrintHelmetExtProps {
    helmetExt: HelmetExtension

    onClose(): void
    onPrint(): void
}
export const PrintHelmetExt: Component<PrintHelmetExtProps> = P => {
    let imgRef: HTMLImageElement

    const [loading, setLoading] = createSignal(true)

    onMount(async () => {
        let h = P.helmetExt

        let objectUrl

        setLoading(true)

        try {
            const res = await helmet_ext_to_image(h)

            objectUrl = URL.createObjectURL(res)

            imgRef.src = objectUrl
        } catch (err) {
            addAlert({
                type: 'error',
                subject: 'خطا!',
                content: err instanceof Error ? err.message : String(err),
                timeout: 3,
            })
            return
        } finally {
        }

        onCleanup(() => {
            objectUrl && URL.revokeObjectURL(objectUrl)
        })
    })

    onCleanup(() => {
        closed()
    })
    const closed = () => {}

    const submitForm = () => {
        if (loading()) {
            addAlert({
                type: 'error',
                subject: 'تصویر هنوز آماده نیست',
                content: 'لطفا کمی صبر کنید تا تصویر بارگذاری شود.',
                timeout: 3,
            })
            return
        }

        if (!imgRef?.src) {
            addAlert({
                type: 'error',
                subject: 'آدرس تصویر موجود نیست',
                content: 'تصویر بارگذاری نشده یا آدرسی موجود نیست.',
                timeout: 3,
            })
            return
        }

        const src = imgRef.src
        const w = window.open('', '_blank')
        if (!w) {
            addAlert({
                type: 'error',
                subject: 'پنجره جدید باز نشد',
                content:
                    'مرورگر اجازه باز کردن پنجره جدید را نداد (popup blocker). لطفا اجازه دهید یا از دکمه راست استفاده کنید.',
                timeout: 4,
            })
            return
        }

        const cleanup = () => {
            try {
                w.removeEventListener &&
                    w.removeEventListener('afterprint', afterPrintHandler)
            } catch {}
        }

        const afterPrintHandler = () => {
            try {
                P.onPrint() // best-effort callback
            } catch (e) {
                /* ignore errors from user callback */
            } finally {
                cleanup()
            }
        }

        try {
            const doc = w.document
            doc.title = 'Print'

            const style = doc.createElement('style')

            style.textContent = `
    @page {
        size: auto;
        margin: 0;
    }

    html,
    body {
        margin: 0;
        padding: 0;
        width: 100%;
        height: 100%;
    }

    body {
        display: flex;
        align-items: center;
        justify-content: center;
        overflow: hidden;
    }

    img {
        display: block;
        width: auto;
        height: auto;
        max-width: 100%;
        max-height: 100%;
        object-fit: contain;
        page-break-inside: avoid;
        break-inside: avoid;
    }

    @media print {
        html,
        body {
            width: 100%;
            height: 100%;
            margin: 0;
            padding: 0;
        }

        img {
            max-width: 100%;
            max-height: 100%;
            width: auto;
            height: auto;
        }
    }
`

            doc.head.appendChild(style)

            const outImg = doc.createElement('img')
            outImg.src = src
            outImg.alt = 'Helmet Image'

            // attach afterprint listener on the popup window
            // note: some browsers fire afterprint on the popup window; keep a fallback below
            try {
                w.addEventListener('afterprint', afterPrintHandler)
            } catch (e) {
                // ignore if not supported
            }

            const doPrintAndClose = () => {
                try {
                    w.focus()
                    w.print()
                } catch (e) {
                    addAlert({
                        type: 'error',
                        subject: 'خطا در چاپ',
                        content: String(e),
                        timeout: 4,
                    })
                } finally {
                    // close the popup shortly after print dialog closes
                    setTimeout(() => {
                        try {
                            w.close()
                        } catch {}
                    }, 200)
                }
            }

            const onLoad = () => {
                // update @page orientation based on image natural size
                // const isLandscape = outImg.naturalWidth > outImg.naturalHeight
                // style.textContent = style.textContent!.replace(
                //     /@page\s*\{[^}]*\}/,
                //     `@page { size: A4 ${isLandscape ? 'landscape' : 'portrait'}; margin: 0; }`
                // )

                doPrintAndClose()
            }

            const onError = () => {
                addAlert({
                    type: 'error',
                    subject: 'خطا در بارگذاری تصویر',
                    content: 'تصویر در صفحه چاپ بارگذاری نشد.',
                    timeout: 4,
                })
                setTimeout(() => {
                    try {
                        w.close()
                    } catch {}
                }, 300)
            }

            outImg.addEventListener('load', onLoad, { once: true })
            outImg.addEventListener('error', onError, { once: true })
            doc.body.appendChild(outImg)
        } catch (err) {
            addAlert({
                type: 'error',
                subject: 'خطا!',
                content: err instanceof Error ? err.message : String(err),
                timeout: 4,
            })
            try {
                w.close()
            } catch {}
        }
    }

    return (
        <div class='print-cmp-container' onclick={P.onClose}>
            <form
                onsubmit={e => {
                    e.preventDefault()
                    submitForm()
                }}
                class='print-cmp-wrapper'
                onclick={e => e.stopPropagation()}
            >
                <div class='scaler'></div>
                <div class='card'>
                    <button
                        class='close-print'
                        type='button'
                        onclick={P.onClose}
                    >
                        <CloseIcon />
                    </button>

                    <div
                        class='img-container'
                        classList={{ loaded: !loading() }}
                    >
                        <LoadingElem class='img-loading' />
                        <img
                            decoding='async'
                            loading='lazy'
                            src=''
                            alt=''
                            onload={() => setLoading(false)}
                            ref={e => (imgRef = e)}
                        />
                    </div>
                    <div class='cta-wrapper'>
                        <button class='title_small main' disabled={loading()}>
                            <PrintIcon />
                            پرینت
                        </button>
                    </div>
                </div>
            </form>
        </div>
    )
}

export default PrintHelmet
